import z from 'zod'
import { amPointSchema } from 'src/common-services/types/alphamap/generic-types'

const amFeatureTypeSchema = z.literal('Feature')
const amFeaturePropertiesSchema = z.record(z.string(), z.string())

export const amPosition = z.array(z.number())

export const amGeometryPointSchema = z.object({
  type: z.literal('Point'),
  coordinates: amPosition,
})

export type AMGeometryPoint = z.infer<typeof amGeometryPointSchema>

export const amGeometryPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(amPosition)),
})

export const amGeometryPolygonWithNameSchema = amGeometryPolygonSchema.extend({
  name: z.string(),
  description: z.string(),
})

export type AMGeometryPolygon = z.infer<typeof amGeometryPolygonSchema>

export const amGeometryMultiPolygonSchema = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: z.array(z.array(z.array(amPosition))),
})

export type AMGeometryMultiPolygon = z.infer<typeof amGeometryMultiPolygonSchema>

export const amGeometrySchema = z.union([amGeometryPointSchema, amGeometryPolygonSchema, amGeometryMultiPolygonSchema])

export type AMGeometry = z.infer<typeof amGeometrySchema>

export const amFeatureSchema = z.object({
  type: amFeatureTypeSchema,
  geometry: amGeometrySchema,
  properties: amFeaturePropertiesSchema,
})

export type AMFeature = z.infer<typeof amFeatureSchema>

export const amMultiPolygonFeatureSchema = z.object({
  type: amFeatureTypeSchema,
  geometry: amGeometryMultiPolygonSchema,
  properties: amFeaturePropertiesSchema,
})

export type AMMultiPolygonFeature = z.infer<typeof amMultiPolygonFeatureSchema>

export const amPolygonFeatureSchema = z.object({
  type: amFeatureTypeSchema,
  geometry: amGeometryPolygonSchema,
  properties: amFeaturePropertiesSchema,
})

export type AMPolygonFeature = z.infer<typeof amPolygonFeatureSchema>

export const amPointFeatureSchema = z.object({
  type: amFeatureTypeSchema,
  geometry: amGeometryPointSchema,
  properties: amFeaturePropertiesSchema,
})

export type AMPointFeature = z.infer<typeof amPointFeatureSchema>

export const amFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(amFeatureSchema),
})

export type AMFeatureCollection = z.infer<typeof amFeatureCollectionSchema>

export const amGeometryMultiPointSchema = z.object({
  type: z.literal('MultiPoint'),
  coordinates: z.array(amPosition),
})
export const amGeometryLineStringSchema = z.object({
  type: z.literal('LineString'),
  coordinates: z.array(amPosition),
})

export const amGeometryMultiLineStringSchema = z.object({
  type: z.literal('MultiLineString'),
  coordinates: z.array(z.array(amPosition)),
})

export const amGeometryCollectionSchema = z.object({
  type: z.literal('GeometryCollection'),
  crs: z.object({
    type: z.literal('name'),
    properties: z.object({
      name: z.string(),
    }),
  }),
  geometries: z
    .array(
      z.union([
        amGeometryPointSchema,
        amGeometryMultiPointSchema,
        amGeometryLineStringSchema,
        amGeometryMultiLineStringSchema,
        amGeometryPolygonSchema,
        amGeometryMultiPolygonSchema,
      ])
    )
    .min(1, 'must have at least one geometry in here, sup'),
})

export const stringOrNumberSchema = z.union([z.string(), z.number()])
export const stringOrNumberPointSchema = z.array(stringOrNumberSchema).length(2)

export const serializedPolygonSchema = amGeometryPolygonSchema
  .pick({
    type: true,
  })
  .extend({ coordinates: z.array(z.array(stringOrNumberPointSchema)) })
  .strict()

export const serializedMultiPolygonSchema = amGeometryMultiPolygonSchema
  .pick({
    type: true,
  })
  .extend({ coordinates: z.array(z.array(z.array(stringOrNumberPointSchema))) })
  .strict()

export const serializedLineStringSchema = amGeometryLineStringSchema
  .pick({
    type: true,
  })
  .extend({ coordinates: z.array(stringOrNumberPointSchema) })
  .strict()

export const serializedMultiLineStringSchema = amGeometryMultiLineStringSchema
  .pick({
    type: true,
  })
  .extend({ coordinates: z.array(z.array(stringOrNumberPointSchema)) })
  .strict()

export const serializedPointSchema = amPointSchema
  .pick({
    type: true,
  })
  .extend({ coordinates: stringOrNumberPointSchema })
  .strict()
export const serializedMultiPointSchema = amGeometryMultiPointSchema
  .pick({
    type: true,
  })
  .extend({ coordinates: z.array(stringOrNumberPointSchema) })
  .strict()

export const serializedGeometryCollectionSchema = z
  .object({
    type: z.literal('GeometryCollection'),
    geometries: z
      .array(
        z.union([
          serializedPolygonSchema,
          serializedMultiPolygonSchema,
          serializedLineStringSchema,
          serializedMultiLineStringSchema,
          serializedPointSchema,
          serializedMultiPointSchema,
        ])
      )
      .min(1, 'must have at least one geometry in here, sup'),
  })
  .strict()
